import { API } from '..';

// Rà truy vấn của user. Backend: cab-user-service, controller SecurityAdmin → route admin/Security.
const BASE = '/v1/user-service/admin/security';

export type RuleType = 'KEYWORD' | 'ATTACK';
export type MatchType = 'CONTAINS' | 'REGEX';
export type Severity = 'LOW' | 'MEDIUM' | 'HIGH';
export type RuleAction = 'LOG' | 'WARN' | 'BLOCK';

export interface SecurityRule {
  id: string;
  name: string;
  ruleType: RuleType;
  matchType: MatchType;
  pattern: string;
  severity: Severity;
  action: RuleAction;
  enabled: boolean;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface QueryLog {
  id: string;
  userId: string | null;
  path: string;
  method: string;
  queryText: string;
  ip: string;
  userAgent: string;
  ruleId: string | null;
  ruleName: string;
  ruleType: string;
  severity: string;
  actionTaken: string;
  reviewed: boolean;
  createdAt: string;
}

export interface QueryLogPage {
  total: number;
  pageNumber: number;
  pageSize: number;
  items: QueryLog[];
}

export interface GuardStats {
  unreviewed: number;
  unreviewedHigh: number;
  last24h: number;
  blocked24h: number;
  activeRules: number;
  // Real-time: cửa sổ 5 phút gần nhất
  queries5m: number;
  violations5m: number;
  activeUsers5m: number;
  serverTime: string;
}

export interface LogQuery {
  pageNumber?: number;
  pageSize?: number;
  userId?: string;
  severity?: string;
  ruleType?: string;
  flaggedOnly?: boolean;
  keyword?: string;
  from?: string;
  to?: string;
}

export type RulePayload = Omit<SecurityRule, 'id' | 'createdAt' | 'updatedAt'>;

export const getRules = (params?: { ruleType?: string; enabled?: boolean }) =>
  API.get<SecurityRule[]>(`${BASE}/rules`, { params }).then((r) => r.data);

export const createRule = (body: RulePayload) =>
  API.post<SecurityRule>(`${BASE}/rules`, body).then((r) => r.data);

export const updateRule = (id: string, body: RulePayload) =>
  API.put<SecurityRule>(`${BASE}/rules/${id}`, body).then((r) => r.data);

export const deleteRule = (id: string) =>
  API.delete(`${BASE}/rules/${id}`).then((r) => r.data);

export const testRules = (text: string) =>
  API.post<{ matched: boolean; rules: { id: string; name: string; ruleType: string; severity: string; action: string }[] }>(
    `${BASE}/rules/test`,
    { text },
  ).then((r) => r.data);

export const getQueryLogs = (params: LogQuery) =>
  API.get<QueryLogPage>(`${BASE}/logs`, { params }).then((r) => r.data);

export const getGuardStats = () =>
  API.get<GuardStats>(`${BASE}/stats`).then((r) => r.data);

export const markReviewed = (ids?: string[]) =>
  API.post<{ updated: number }>(`${BASE}/logs/review`, { ids }).then((r) => r.data);

export interface AlertPreview {
  emailConfigured: boolean;
  sendTo: string | null;
  minSeverity: string;
  intervalMinutes: number;
  windowMinutes: number;
  total: number;
  blocked: number;
  subject: string;
  html: string;
}

export const getAlertPreview = (minutes = 1440) =>
  API.get<AlertPreview>(`${BASE}/alert-preview`, { params: { minutes } }).then((r) => r.data);

export const sendAlertTest = (minutes = 1440) =>
  API.post<{ sent: boolean; to: string; total: number }>(`${BASE}/alert-test`, null, {
    params: { minutes },
  }).then((r) => r.data);

// ─────────── Thư viện mẫu + Chặn IP/tài khoản (controller AccessBlockAdmin) ───────────
const BASE_BLOCK = '/v1/user-service/admin/accessblock';

export interface LibraryItem {
  key: string;
  name: string;
  category: string;
  ruleType: RuleType;
  matchType: MatchType;
  pattern: string;
  severity: Severity;
  note: string;
  isDefault: boolean;
  added: boolean;
}

export interface LibraryResponse {
  total: number;
  categories: string[];
  items: LibraryItem[];
}

export type BlockType = 'IP' | 'USER';

export interface AccessBlock {
  id: string;
  blockType: BlockType;
  value: string;
  reason: string;
  expiresAt: string | null;
  enabled: boolean;
  createdBy: string;
  createdAt: string;
}

export const getLibrary = () =>
  API.get<LibraryResponse>(`${BASE_BLOCK}/library`).then((r) => r.data);

export const addFromLibrary = (key: string) =>
  API.post<SecurityRule>(`${BASE_BLOCK}/library/${encodeURIComponent(key)}/add`).then((r) => r.data);

export const getBlocks = (params?: { blockType?: string; activeOnly?: boolean }) =>
  API.get<AccessBlock[]>(`${BASE_BLOCK}/blocks`, { params }).then((r) => r.data);

export const addBlock = (body: {
  blockType: BlockType;
  value: string;
  reason?: string;
  expiresInHours?: number | null;
  createdBy?: string;
}) => API.post<AccessBlock>(`${BASE_BLOCK}/blocks`, body).then((r) => r.data);

export const removeBlock = (id: string) =>
  API.delete(`${BASE_BLOCK}/blocks/${id}`).then((r) => r.data);
